import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Option,
  Select,
  Typography,
} from '@mui/joy';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CRM_TEMPLATES_KEY,
  CrmTemplatesAPI,
} from '../../../lib/apiCrmTemplates';
import type {
  CrmEmailTemplate,
  CrmMessagePreview,
} from '../../../lib/apiCrmTemplates';
import { AdminSequencesAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import { useDraftGuard } from './useDraftGuard';
import { useProfileDraft } from '../organizations/ProfileDrafts';

export function CrmTemplatePicker({
  leadId,
  organizationId,
  recipient,
  hasDraft,
  disabled,
  onApply,
  onManage,
  onBusyChange,
}: {
  leadId?: string;
  organizationId?: string;
  recipient?: string;
  hasDraft: boolean;
  disabled: boolean;
  onBusyChange?: (busy: boolean) => void;
  onApply: (preview: CrmMessagePreview) => void;
  onManage: () => void;
}) {
  const templates = useQuery({
    queryKey: CRM_TEMPLATES_KEY,
    queryFn: CrmTemplatesAPI.list,
  });
  const sequenceTemplates = useQuery({
    queryKey: ['admin', 'email-templates', 'all'],
    enabled: !!organizationId,
    queryFn: async () => {
      const rows: CrmEmailTemplate[] = [];
      let page = 1;
      while (true) {
        const result = await AdminSequencesAPI.listTemplates(page);
        rows.push(
          ...result.results.map((row) => ({
            ...row,
            id: `sequence:${row.id}`,
          })),
        );
        if (!result.next) return rows;
        page += 1;
      }
    },
  });
  const options = [
    ...(templates.data || []),
    ...(sequenceTemplates.data || []),
  ];
  const [applied, setApplied] = useState('');
  const preview = useMutation({
    mutationFn: (row: CrmEmailTemplate) =>
      organizationId
        ? CrmTemplatesAPI.preview(row.subject, row.body_text, undefined, {
            organization_id: organizationId,
            recipient: (recipient || '').trim(),
          }, row.body_html)
        : CrmTemplatesAPI.preview(row.subject, row.body_text, leadId, undefined, row.body_html),
    onSuccess: (data, row) => {
      onApply(data);
      setApplied(row.name);
    },
  });
  useEffect(() => {
    onBusyChange?.(preview.isPending);
    return () => onBusyChange?.(false);
  }, [preview.isPending, onBusyChange]);
  const guard = useDraftGuard(hasDraft, disabled || preview.isPending);
  useProfileDraft('crm-template-preview', false, preview.isPending);
  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <Box
        sx={{ display: 'flex', alignItems: 'end', gap: 1, flexWrap: 'wrap' }}
      >
        <FormControl sx={{ flex: '1 1 220px', minWidth: 0 }}>
          <FormLabel>Email template</FormLabel>
          <Select
            value={null}
            placeholder={
              preview.isPending
                ? 'Personalizing…'
                : 'Choose a template (optional)'
            }
            disabled={disabled || preview.isPending || templates.isPending}
            onChange={(_, id) => {
              const row = options.find((template) => template.id === id);
              if (row) guard.request(() => preview.mutate(row));
            }}
          >
            {options.map((row) => (
              <Option key={row.id} value={row.id}>
                {row.name}
                {row.id.startsWith('sequence:') ? ' · Onboarding' : ''}
              </Option>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          color="neutral"
          onClick={onManage}
          disabled={disabled || preview.isPending}
        >
          Manage templates
        </Button>
      </Box>
      {templates.isError && (
        <Alert color="danger">
          Templates could not be loaded.
          <Button onClick={() => templates.refetch()}>Retry templates</Button>
        </Alert>
      )}
      {templates.data &&
        options.length === 0 &&
        !sequenceTemplates.isFetching && (
          <Typography level="body-xs">
            No templates yet. Use Manage templates to create one, or write your
            own email below.
          </Typography>
        )}
      {organizationId && (
        <Typography level="body-xs">
          Choose a Sales template or an onboarding sequence template. The
          message uses this account’s selected recipient.
        </Typography>
      )}
      {organizationId && sequenceTemplates.isError && (
        <Alert color="warning">
          Onboarding templates could not be loaded.
          <Button onClick={() => sequenceTemplates.refetch()}>
            Retry onboarding templates
          </Button>
        </Alert>
      )}
      {preview.isError && (
        <Alert color="danger">
          {getApiErrorMessage(
            preview.error,
            'Could not personalize this template. Your draft has been kept.',
          )}
        </Alert>
      )}
      {applied && (
        <Typography role="status" level="body-xs">
          “{applied}” filled in for this recipient. Review and edit the message
          below before sending.
        </Typography>
      )}
      {preview.data?.warnings.map((warning) => (
        <Alert key={warning} color="warning" size="sm">
          {warning}
        </Alert>
      ))}
      {guard.dialog}
    </Box>
  );
}
